import axios from 'axios';

const API_BASE_URL = 'http://localhost:3001/api';

export interface AdvancedScoringRequest {
  application: any;
}

export interface AdvancedScoringResponse {
  success: boolean;
  advancedScore: any;
  fraudDetectionResult: any;
  riskFactors: any[];
}

export interface PricingRequest {
  creditScore: number;
  loanAmount: number;
  loanTerm: number;
  customerType: 'individual' | 'sme';
  sector?: string;
}

export interface PricingResponse {
  success: boolean;
  pricing: any;
}

export interface RiskAnalysisResponse {
  application: any;
  repayments: any[];
  riskMetrics: {
    overduePayments: number;
    completedPayments: number;
    totalPayments: number;
    paymentReliability: number;
    riskLevel: 'low' | 'medium' | 'high';
  };
}

export interface PortfolioRiskResponse {
  success: boolean;
  portfolioRisk: {
    totalLoans: number;
    riskDistribution: {
      low: number;
      medium: number;
      high: number;
    };
    riskPercentages: {
      low: number;
      medium: number;
      high: number;
    };
    paymentMetrics: {
      totalRepayments: number;
      completedRepayments: number;
      overdueRepayments: number;
      paymentReliability: number;
    };
    averageCreditScore: number;
  };
}

class RiskManagementApi {
  /**
   * Get advanced credit scoring with fraud detection
   */
  async getAdvancedScoring(application: any): Promise<AdvancedScoringResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/risk/advanced-scoring`, {
        application
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching advanced scoring:', error);
      throw error;
    }
  }

  /**
   * Get risk-based pricing
   */
  async getRiskBasedPricing(request: PricingRequest): Promise<PricingResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/risk/pricing`, request);
      return response.data;
    } catch (error) {
      console.error('Error fetching risk-based pricing:', error);
      throw error;
    }
  }

  /**
   * Get risk analysis for a specific application
   */
  async getApplicationRiskAnalysis(applicationId: string): Promise<RiskAnalysisResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/risk/application/${applicationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching application risk analysis:', error);
      throw error;
    }
  }

  /**
   * Get portfolio risk summary
   */
  async getPortfolioRiskSummary(params?: {
    merchantId?: string;
    start?: string;
    end?: string;
  }): Promise<PortfolioRiskResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.merchantId) queryParams.append('merchantId', params.merchantId);
      if (params?.start) queryParams.append('start', params.start);
      if (params?.end) queryParams.append('end', params.end);

      const response = await axios.get(`${API_BASE_URL}/risk/portfolio-summary?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio risk summary:', error);
      throw error;
    }
  }

  /**
   * Get applications for risk analysis
   */
  async getApplications(params?: {
    merchantId?: string;
    start?: string;
    end?: string;
    page?: number;
    limit?: number;
    status?: string;
  }) {
    try {
      const queryParams = new URLSearchParams();
      if (params?.merchantId) queryParams.append('merchantId', params.merchantId);
      if (params?.start) queryParams.append('start', params.start);
      if (params?.end) queryParams.append('end', params.end);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const response = await axios.get(`${API_BASE_URL}/applications?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  }

  /**
   * Get metrics summary
   */
  async getMetricsSummary(params?: {
    merchantId?: string;
    start?: string;
    end?: string;
  }) {
    try {
      const queryParams = new URLSearchParams();
      if (params?.merchantId) queryParams.append('merchantId', params.merchantId);
      if (params?.start) queryParams.append('start', params.start);
      if (params?.end) queryParams.append('end', params.end);

      const response = await axios.get(`${API_BASE_URL}/metrics/summary?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching metrics summary:', error);
      throw error;
    }
  }
}

export default new RiskManagementApi();
